import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { MapPin, Phone, Clock, ExternalLink } from "lucide-react";

// Custom marker icons (avoid missing default icon assets)
const restaurantIcon = L.divIcon({
  className: "",
  html: `<div style="background:#8B3A3A;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.25);"><div style="width:8px;height:8px;background:#fff;border-radius:50%"></div></div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const attractionIcon = L.divIcon({
  className: "",
  html: `<div style="background:#2C2C2A;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid #FAF9F6;box-shadow:0 2px 6px rgba(0,0,0,0.2);"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

export default function Location() {
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data));
  }, []);

  if (!info) return <div className="py-32 text-center text-[#5C5C59]">…</div>;

  const center = [info.lat, info.lng];
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(info.address)}`;

  return (
    <div data-testid="location-page" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
              {t("location.eyebrow")}
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-none mt-5">
              {t("location.title")}
            </h1>
            <p className="text-[#5C5C59] mt-6 leading-relaxed">{t("location.subtitle")}</p>

            <div className="mt-10 space-y-6 border-t border-[#E5E3D8] pt-8">
              <Detail icon={MapPin} label={t("location.address")} value={info.address} />
              <Detail icon={Phone} label={t("location.phone")} value={info.phone} link={`tel:${info.phone}`} />
              <Detail icon={Clock} label={t("location.hours")} value={info.hours[i18n.language] || info.hours[{ at: "de", ar: "en" }[i18n.language]] || info.hours.it} />
            </div>

            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="open-in-maps"
              className="mt-8 inline-flex items-center gap-2 text-sm tracking-wide text-[#8B3A3A] link-underline"
            >
              {t("location.openInMaps")} <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>

          <div className="lg:col-span-7">
            <div data-testid="map-container" className="aspect-square sm:aspect-[4/3] lg:aspect-square w-full border border-[#E5E3D8]">
              <MapContainer center={center} zoom={15} scrollWheelZoom={false} style={{ width: "100%", height: "100%" }}>
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={center} icon={restaurantIcon}>
                  <Popup>
                    <strong>Harry's Salotto e Cucina</strong>
                    <br />{info.address}
                  </Popup>
                </Marker>
                {info.attractions.map((a, idx) => (
                  <Marker key={idx} position={[a.lat, a.lng]} icon={attractionIcon}>
                    <Popup>{a.name}</Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>

        {/* Attractions list */}
        <div className="mt-20">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">{t("location.attractions")}</h2>
          <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {info.attractions.map((a, idx) => (
              <li
                key={idx}
                data-testid={`attraction-${idx}`}
                className="bg-[#F3F1EC] border border-[#E5E3D8] p-5"
              >
                <div className="text-xs tracking-[0.2em] uppercase text-[#8B3A3A] font-semibold">
                  {a.type}
                </div>
                <div className="font-serif text-xl mt-2 leading-tight">{a.name}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value, link }) {
  return (
    <div className="flex gap-4">
      <Icon className="w-5 h-5 text-[#8B3A3A] mt-0.5" strokeWidth={1.5} />
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#5C5C59] font-semibold">{label}</div>
        {link ? (
          <a href={link} className="link-underline mt-1 inline-block">{value}</a>
        ) : (
          <div className="mt-1">{value}</div>
        )}
      </div>
    </div>
  );
}
